<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { vMaska } from 'maska/vue';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import { PhoneNumber } from '@/models/PhoneNumber';
import { watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

interface DropdownOption {
  name: string;
  dialCode: string;
  icon: string;
  code: string;
}

const emit = defineEmits<{
  (e: 'prefixSelected', prefix: DropdownOption): void;
  (e: 'blur'): void;
}>();

const props = defineProps({
  dropdownOptions: {
    type: Array as () => Array<DropdownOption>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  setPhoneNumberError: {
    type: Function,
    required: true,
  },
  isError: {
    type: Boolean,
    default: false,
  },
});

const phoneNumber = defineModel('phoneNumber', {
  type: String,
  default: '',
});

const phonePrefix = defineModel('phonePrefix', {
  type: Object as () => DropdownOption,
  default: null,
});

watch(phoneNumber, () => {
  if (!props.isError) return;
  validateAndParsePhoneNumber();
});

watch(phonePrefix, () => {
  validateAndParsePhoneNumber();
});

function onPhoneInputBlur() {
  emit('blur');
  validateAndParsePhoneNumber();
}

function resolveDropdownOption(dialCode: string): DropdownOption | undefined {
  return props.dropdownOptions.find((option) => option.dialCode === dialCode);
}

const validateAndParsePhoneNumber = useDebounceFn(() => {
  if (!phoneNumber.value || !phonePrefix.value) return;

  const parsedPhoneNumber = PhoneNumber.parse({
    phoneNumber: phoneNumber.value,
    allowedDialCodes: props.dropdownOptions,
    defaultRegionCode: phonePrefix.value.code,
  });

  if (!parsedPhoneNumber) {
    props.setPhoneNumberError(true);
    return;
  }
  props.setPhoneNumberError(false);

  const existingPhoneNumber = PhoneNumber.create({
    phoneNumber: phoneNumber.value,
    prefix: phonePrefix.value.dialCode,
    regionCode: phonePrefix.value.code,
  });

  const comparison = PhoneNumber.compare({
    phoneNumberA: parsedPhoneNumber,
    phoneNumberB: existingPhoneNumber,
  });

  //keyed value is different to the existing phone number. update the displayed phone number input value
  if (comparison.result === 'SAME') {
    return;
  }

  if (comparison.result === 'DIFFERENT') {
    if (comparison.parts.phoneNumber === 'DIFFERENT') {
      phoneNumber.value = parsedPhoneNumber.phoneNumber;
    }
    if (comparison.parts.prefix === 'DIFFERENT') {
      const newPrefixDropdown = resolveDropdownOption(parsedPhoneNumber.prefix);

      if (!newPrefixDropdown) {
        return;
      }
      phonePrefix.value = newPrefixDropdown;

      emit('prefixSelected', newPrefixDropdown);
    }
  }
}, 800);
</script>

<template>
  <div
    class="border-light-border dark:border-dark-semi-transparent dark:bg-dark-list-bg flex h-12 items-center rounded-lg border"
  >
    <!-- Dropdown -->
    <Listbox
      v-model="phonePrefix"
      v-if="phonePrefix"
      class="flex h-full"
      :disabled="disabled"
      data-test="dropdown-phone-prefix"
      @update:modelValue="(value) => emit('prefixSelected', value)"
    >
      <div class="relative block">
        <ListboxButton
          class="hover:bg-light-list-bg dark:hover:bg-dark-black relative flex w-full cursor-pointer items-center rounded-md py-2 pr-5 pl-3 text-left transition-all focus:outline-none disabled:cursor-default disabled:bg-transparent"
          name="phone-prefix"
          aria-placeholder="Select country code"
          data-test="button-phone-prefix"
        >
          <span
            :aria-hidden="!phonePrefix.dialCode"
            data-test="button-phone-prefix-span"
            :class="['text-light-primary dark:text-dark-list-text mr-3 block text-base']"
            >{{ phonePrefix.dialCode ? phonePrefix.dialCode : '+xx' }}</span
          >

          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronRightIcon class="w-4 rotate-90" />
          </span>
        </ListboxButton>

        <transition
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            id="phone-prefix-options"
            class="bg-light-list-bg dark:bg-dark-list-bg absolute top-14 z-10 max-h-60 overflow-x-clip overflow-y-auto rounded-md text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
            data-test="dropdown-phone-prefixes"
          >
            <ListboxOption
              v-slot="{ selected }"
              v-for="option in dropdownOptions"
              :key="option.dialCode"
              :value="option"
            >
              <li
                data-test="phone-prefix-option"
                :class="[
                  selected ? 'bg-light-select dark:bg-dark-black dark:text-white' : '',
                  'text-light-primary dark:text-dark-primary relative flex max-w-xs cursor-pointer overflow-x-clip rounded py-2 pr-4 pl-4 select-none hover:bg-slate-50 sm:max-w-full',
                ]"
              >
                <span :class="['pr-2']">{{ option.icon }}</span>
                <span :class="[selected ? 'font-bold' : 'font-normal', 'block truncate']">
                  {{ option.name }}
                  ({{ option.dialCode }})
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

    <!-- Phone Input -->
    <input
      name="phone-number"
      id="phone-number-input"
      type="tel"
      inputmode="tel"
      class="border-light-border text-light-primary dark:border-dark-semi-transparent dark:text-dark-list-text box-border flex h-full w-full border-l bg-transparent pl-4 text-base outline-none"
      placeholder="xx xxx xxxx"
      v-model="phoneNumber"
      v-maska
      data-maska="+##############"
      data-maska-tokens="+:[+]:optional"
      :disabled="disabled"
      autocomplete="tel"
      data-test="input-phone"
      @input="validateAndParsePhoneNumber"
      @blur="onPhoneInputBlur"
      @mouseleave="validateAndParsePhoneNumber"
    />
  </div>
</template>
